import type { SvgIconComponent } from 'components/icons';
import { cn } from 'utils/cn';

/** A rule a text breaks, or a thing to still do: the sentence, and its code when it has one. */
interface FindingRule {
    code?: string | null;
    reason?: string | null;
}

const LIST_ITEM_CLASS = 'text-meta flex items-start gap-2';
const MARKER_CLASS = 'mt-[3px] size-3.5';

/**
 * A finding as a sentence: its own full stop dropped, then its code in brackets, a generic code in grey and a firm's
 * in the firm colour, then the full stop. An item that completes "Add:" takes no full stop.
 */
function Finding({ rule, withStop }: { rule: FindingRule; withStop: boolean }) {
    const sentence = (rule.reason ?? '').replace(/\.\s*$/, '');
    return (
        <span className="min-w-0 break-words">
            {sentence}
            {rule.code && (
                <>
                    {' '}
                    <span
                        className={
                            /^G\d+$/.test(rule.code)
                                ? 'text-grey-600 new:text-muted-foreground font-medium'
                                : 'text-primary new:text-primary-ink font-semibold'
                        }
                    >
                        ({rule.code})
                    </span>
                </>
            )}
            {withStop && '.'}
        </span>
    );
}

type FindingListProps = {
    rules: FindingRule[];
    Marker: SvgIconComponent;
    markerClassName: string;
    withStop?: boolean;
    className?: string;
};

export function FindingList({ rules, Marker, markerClassName, withStop = true, className }: FindingListProps) {
    return (
        <ul className={cn('flex flex-col gap-1', className)}>
            {/* The model may repeat an item, and each answer mounts a list of its own. */}
            {rules.map((rule, index) => (
                <li key={index} className={LIST_ITEM_CLASS}>
                    <Marker className={cn(MARKER_CLASS, markerClassName)} />
                    <Finding rule={rule} withStop={withStop} />
                </li>
            ))}
        </ul>
    );
}
